import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Organization } from '../types';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { logger } from '../utils/logger';

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  setCurrentOrganization: (org: Organization | null) => void;
  loadOrganizations: () => Promise<Organization[]>;
  loading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  const loadOrganizations = useCallback(async (): Promise<Organization[]> => {
    // Evitar múltiplas chamadas simultâneas
    if (loadingRef.current) {
      logger.debug('[OrganizationContext] Already loading organizations, skipping...');
      return [];
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      logger.debug('[OrganizationContext] Loading organizations...');
      const response = await api.get<Organization[]>('/organizations');
      const loadedOrgs = response.data || [];
      logger.debug('[OrganizationContext] Loaded organizations:', loadedOrgs);
      setOrganizations(loadedOrgs);
      
      // Cada usuário tem apenas uma organização, então selecionar a primeira (e única)
      if (loadedOrgs.length > 0) {
        const org = loadedOrgs[0];
        setCurrentOrganization((prevOrg) => {
          // Só atualizar se for diferente da atual para evitar loops
          if (!prevOrg || prevOrg.id !== org.id) {
            logger.debug('[OrganizationContext] Setting current organization:', org);
            localStorage.setItem('currentOrganizationId', org.id);
            return org;
          }
          return prevOrg;
        });
      } else {
        logger.debug('[OrganizationContext] No organizations found');
        setCurrentOrganization((prevOrg) => {
          if (prevOrg) {
            localStorage.removeItem('currentOrganizationId');
            return null;
          }
          return prevOrg;
        });
      }
      
      return loadedOrgs;
    } catch (error) {
      console.error('[OrganizationContext] Error loading organizations:', error);
      setOrganizations([]);
      setCurrentOrganization((prevOrg) => {
        if (prevOrg) {
          localStorage.removeItem('currentOrganizationId');
          return null;
        }
        return prevOrg;
      });
      return [];
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  const userIdRef = useRef<string | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const currentUserId = user?.uid || null;
    
    // Só carregar se o userId mudou ou se ainda não carregou
    if (!authLoading && user && currentUserId !== userIdRef.current) {
      userIdRef.current = currentUserId;
      hasLoadedRef.current = false;
      // Só carregar se ainda não tem organização ou se a organização não pertence ao usuário atual
      if (!currentOrganization || currentOrganization.ownerId !== currentUserId) {
        loadOrganizations();
      }
    } else if (!authLoading && !user) {
      // Se não há usuário, limpar organização
      userIdRef.current = null;
      hasLoadedRef.current = false;
      setCurrentOrganization(null);
      setOrganizations([]);
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, authLoading]);

  useEffect(() => {
    if (currentOrganization) {
      localStorage.setItem('currentOrganizationId', currentOrganization.id);
    }
  }, [currentOrganization]);

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        setCurrentOrganization,
        loadOrganizations,
        loading,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

